import type { ReactNode } from 'react';

// ============================================================================
// Amenity icon + photo-keyword catalog — single source of truth
// ============================================================================
//
// Vendored from the Verso theme's `_amenityIcons` so the `AmenitiesForYou`
// section here can reuse the exact icon geometry + photo-keyword catalog.
// Copied (not deep-imported) because the source lives in an unrelated repo and
// the sibling theme package does not export its `_amenityIcons` helper — a
// bare deep import won't resolve under the bundler typecheck.
//
// Property amenities rarely ship their own icon, so we infer a relevant one
// from the amenity's title. Each category also carries `photoKeywords` used to
// pick a *relevant* gallery photo for the amenity (see TabbedCallouts) — this
// prevents mismatches like a "Concierge Coffee House" amenity showing a model
// apartment interior.
//
// Icons are stroke-based (Lucide geometry) and draw with `currentColor`, so the
// surrounding card styles control their color exactly like the previous
// fallback checkmark did.

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      className="da-card-icon"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

interface AmenityCategory {
  /** Substrings that, when found in an amenity title, select this category. */
  match: string[];
  /** Words to look for in gallery photo text to find a relevant image. */
  photoKeywords: string[];
  Icon: () => ReactNode;
}

// Ordered by specificity — the first category whose `match` hits wins.
const CATEGORIES: AmenityCategory[] = [
  {
    match: ['fitness', 'gym', 'workout', 'exercise', 'yoga', 'wellness'],
    photoKeywords: ['fitness', 'gym', 'workout', 'exercise', 'yoga'],
    Icon: () => (
      <Svg>
        <path d="M14.4 14.4 9.6 9.6" />
        <path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829z" />
        <path d="m21.5 21.5-1.4-1.4" />
        <path d="M3.9 3.9 2.5 2.5" />
        <path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z" />
      </Svg>
    ),
  },
  {
    match: ['pool', 'swim'],
    photoKeywords: ['pool', 'swim', 'water'],
    Icon: () => (
      <Svg>
        <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
        <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
        <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      </Svg>
    ),
  },
  {
    match: ['dog', 'pet', 'paw', 'bark'],
    photoKeywords: ['dog', 'pet', 'paw', 'park'],
    Icon: () => (
      <Svg>
        <circle cx="11" cy="4" r="2" />
        <circle cx="18" cy="8" r="2" />
        <circle cx="20" cy="16" r="2" />
        <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" />
      </Svg>
    ),
  },
  {
    match: ['washer', 'dryer', 'laundry', 'washing'],
    photoKeywords: ['laundry', 'washer', 'dryer'],
    Icon: () => (
      <Svg>
        <rect x="3" y="2" width="18" height="20" rx="2" />
        <path d="M6 6h.01" />
        <path d="M10 6h.01" />
        <circle cx="12" cy="13" r="5" />
        <path d="M12 18a2.5 2.5 0 0 0 0-5 2.5 2.5 0 0 1 0-5" />
      </Svg>
    ),
  },
  {
    match: ['wifi', 'wi-fi', 'internet', 'fiber'],
    photoKeywords: ['wifi', 'internet'],
    Icon: () => (
      <Svg>
        <path d="M12 20h.01" />
        <path d="M2 8.82a15 15 0 0 1 20 0" />
        <path d="M5 12.859a10 10 0 0 1 14 0" />
        <path d="M8.5 16.429a5 5 0 0 1 7 0" />
      </Svg>
    ),
  },
  {
    match: ['parking', 'garage', 'carport', 'charging', 'ev '],
    photoKeywords: ['parking', 'garage', 'car'],
    Icon: () => (
      <Svg>
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
        <circle cx="7" cy="17" r="2" />
        <path d="M9 17h6" />
        <circle cx="17" cy="17" r="2" />
      </Svg>
    ),
  },
  {
    match: ['hardwood', 'floor', 'flooring', 'tile', 'carpet', 'plank'],
    photoKeywords: ['floor', 'hardwood', 'interior'],
    Icon: () => (
      <Svg>
        <rect x="3" y="3" width="18" height="18" rx="1" />
        <path d="M3 9h18" />
        <path d="M3 15h18" />
        <path d="M9 3v6" />
        <path d="M15 9v6" />
        <path d="M9 15v6" />
      </Svg>
    ),
  },
  {
    match: ['fan', 'air', 'hvac', 'heating', 'cooling', 'climate', 'thermostat'],
    photoKeywords: ['fan', 'air'],
    Icon: () => (
      <Svg>
        <path d="M12.8 19.6A2 2 0 1 0 14 16H2" />
        <path d="M17.5 8a2.5 2.5 0 1 1 2 4H2" />
        <path d="M9.8 4.4A2 2 0 1 1 11 8H2" />
      </Svg>
    ),
  },
  {
    match: ['coffee', 'concierge', 'cafe', 'espresso', 'barista'],
    photoKeywords: ['coffee', 'cafe', 'concierge', 'lounge'],
    Icon: () => (
      <Svg>
        <path d="M10 2v2" />
        <path d="M14 2v2" />
        <path d="M6 2v2" />
        <path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1" />
      </Svg>
    ),
  },
  {
    match: [
      'kitchen', 'appliance', 'stainless', 'granite', 'quartz', 'island',
      'dishwasher', 'refrigerator', 'microwave', 'oven', 'stove', 'pantry',
    ],
    photoKeywords: ['kitchen', 'appliance'],
    Icon: () => (
      <Svg>
        <path d="M3 2v7c0 1.1.9 2 2 2a2 2 0 0 0 2-2V2" />
        <path d="M7 2v20" />
        <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
      </Svg>
    ),
  },
  {
    match: ['security', 'gated', 'gate', 'controlled access', 'key fob', 'surveillance'],
    photoKeywords: ['security', 'gate'],
    Icon: () => (
      <Svg>
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      </Svg>
    ),
  },
  {
    match: ['package', 'parcel', 'mail', 'locker'],
    photoKeywords: ['package', 'mail'],
    Icon: () => (
      <Svg>
        <path d="m7.5 4.27 9 5.15" />
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="M3.3 7 12 12l8.7-5" />
        <path d="M12 22V12" />
      </Svg>
    ),
  },
  {
    match: ['fireplace', 'fire pit', 'firepit', 'fire'],
    photoKeywords: ['fireplace', 'fire'],
    Icon: () => (
      <Svg>
        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
      </Svg>
    ),
  },
  {
    match: ['spa', 'sauna', 'steam', 'massage'],
    photoKeywords: ['spa', 'sauna'],
    Icon: () => (
      <Svg>
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      </Svg>
    ),
  },
  {
    match: ['courtyard', 'garden', 'patio', 'balcony', 'outdoor', 'grill', 'bbq', 'terrace', 'rooftop', 'landscap'],
    photoKeywords: ['courtyard', 'garden', 'patio', 'rooftop', 'terrace', 'outdoor', 'grill'],
    Icon: () => (
      <Svg>
        <path d="M10 10v.2A3 3 0 0 1 8.9 16H5a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z" />
        <path d="M7 16v6" />
        <path d="M13 19v3" />
        <path d="M12 19h8.3a1 1 0 0 0 .7-1.7L18 14h.3a1 1 0 0 0 .7-1.7L16 9h.2a1 1 0 0 0 .8-1.7L13 3l-1.4 1.5" />
      </Svg>
    ),
  },
  {
    match: ['theater', 'theatre', 'media', 'cinema', 'movie', 'game room', 'gaming', 'arcade'],
    photoKeywords: ['theater', 'media', 'cinema', 'game'],
    Icon: () => (
      <Svg>
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M10 7.75a.75.75 0 0 1 1.142-.638l3.664 2.249a.75.75 0 0 1 0 1.278l-3.664 2.25A.75.75 0 0 1 10 12.25z" />
        <path d="M12 17v4" />
        <path d="M8 21h8" />
      </Svg>
    ),
  },
  {
    match: ['business', 'coworking', 'co-working', 'office', 'conference', 'study', 'work lounge', 'workspace'],
    photoKeywords: ['business', 'office', 'work', 'conference'],
    Icon: () => (
      <Svg>
        <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        <rect x="2" y="6" width="20" height="14" rx="2" />
      </Svg>
    ),
  },
  {
    match: ['tennis', 'pickleball', 'pickle', 'basketball', 'sport', 'volleyball', 'racquet'],
    photoKeywords: ['tennis', 'basketball', 'sport', 'court'],
    Icon: () => (
      <Svg>
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
      </Svg>
    ),
  },
  {
    match: ['playground', 'kids', 'children', 'tot ', 'play area', 'play room', 'playroom'],
    photoKeywords: ['playground', 'kids', 'children'],
    Icon: () => (
      <Svg>
        <path d="M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 19.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1-.474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L7.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L3.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1 .474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z" />
      </Svg>
    ),
  },
  {
    match: ['bike', 'bicycle', 'cycling'],
    photoKeywords: ['bike', 'bicycle'],
    Icon: () => (
      <Svg>
        <circle cx="18.5" cy="17.5" r="3.5" />
        <circle cx="5.5" cy="17.5" r="3.5" />
        <circle cx="15" cy="5" r="1" />
        <path d="M12 17.5V14l-3-3 4-3 2 3h2" />
      </Svg>
    ),
  },
  {
    match: ['smart', 'keyless', 'keypad', 'key fob', 'digital lock', 'nest', 'thermostat control'],
    photoKeywords: ['smart', 'keyless'],
    Icon: () => (
      <Svg>
        <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
        <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
      </Svg>
    ),
  },
  {
    match: ['trash', 'valet trash', 'recycling', 'recycle', 'waste'],
    photoKeywords: ['trash', 'recycling'],
    Icon: () => (
      <Svg>
        <path d="M3 6h18" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
      </Svg>
    ),
  },
  {
    match: ['elevator', 'lift'],
    photoKeywords: ['elevator'],
    Icon: () => (
      <Svg>
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <path d="M9 8l3-3 3 3" />
        <path d="M9 16l3 3 3-3" />
      </Svg>
    ),
  },
  {
    match: ['storage', 'closet', 'walk-in', 'pantry storage'],
    photoKeywords: ['storage', 'closet'],
    Icon: () => (
      <Svg>
        <rect width="20" height="5" x="2" y="3" rx="1" />
        <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
        <path d="M10 12h4" />
      </Svg>
    ),
  },
  {
    match: ['view', 'skyline', 'scenic', 'panoram'],
    photoKeywords: ['view', 'skyline'],
    Icon: () => (
      <Svg>
        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
        <circle cx="12" cy="12" r="3" />
      </Svg>
    ),
  },
  {
    // Apartment / floor-plan / unit-type amenities. Kept late in the list so
    // more specific "unit" contexts (Storage Unit, In-Unit Laundry, etc.) claim
    // their own category first; a bare "Spacious Units" / "Floor Plans" /
    // "Studio" amenity lands here and pulls an apartment-interior standout photo.
    match: ['apartment', 'unit', 'floor plan', 'floorplan', 'layout', 'studio', 'residence'],
    photoKeywords: ['apartment', 'interior', 'living', 'kitchen', 'bedroom', 'dining', 'model', 'residence'],
    Icon: () => (
      <Svg>
        <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
        <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      </Svg>
    ),
  },
  {
    match: ['clubhouse', 'lounge', 'club', 'resident', 'sofa', 'seating'],
    photoKeywords: ['clubhouse', 'lounge', 'club'],
    Icon: () => (
      <Svg>
        <path d="M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3" />
        <path d="M3 11v5a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v2H7v-2a2 2 0 0 0-4 0Z" />
        <path d="M5 18v2" />
        <path d="M19 18v2" />
      </Svg>
    ),
  },
];

// Generic "included" checkmark — matches the section's previous fallback.
function DefaultIcon() {
  return (
    <Svg>
      <polyline points="20 6 9 17 4 12" />
    </Svg>
  );
}

function categorize(title: string | null | undefined): AmenityCategory | null {
  const t = (title || '').toLowerCase();
  if (!t) return null;
  for (const category of CATEGORIES) {
    if (category.match.some((m) => t.includes(m))) return category;
  }
  return null;
}

/** Render an amenity-relevant icon inferred from the title (falls back to a
 *  neutral checkmark when nothing matches). */
export function AmenityIcon({ title }: { title?: string | null }) {
  const category = categorize(title);
  const Icon = category?.Icon ?? DefaultIcon;
  return <Icon />;
}

// Generic residential / marketing words that appear across unrelated photos and
// amenities, so they must never drive a photo match on their own. (e.g. a
// mailroom caption mentioning "units" was picking that photo for a floor-plan
// amenity — "unit" was a stopword but the plural "units" leaked through.)
const PHOTO_STOPWORDS = new Set([
  'and', 'the', 'with', 'for', 'in', 'our', 'your', 'all', 'any',
  'high', 'tech', 'style', 'access', 'new', 'now',
  'unit', 'units', 'apartment', 'apartments', 'home', 'homes', 'house',
  'residence', 'residences', 'resident', 'living', 'floor', 'floors',
  'plan', 'plans', 'layout', 'layouts', 'bedroom', 'bedrooms', 'bath',
  'baths', 'bathroom', 'bathrooms', 'room', 'rooms', 'space', 'spaces',
  'spacious', 'feature', 'features', 'featuring', 'community', 'available',
  'include', 'includes', 'included', 'offer', 'offers', 'onsite', 'site',
  'private', 'modern', 'luxury', 'luxurious', 'premium', 'elegant',
  'beautiful', 'stunning', 'brand', 'newly', 'renovated', 'designer',
  'designed', 'design', 'quality', 'stylish', 'comfort', 'comfortable',
]);

/** Keyword sources for finding a gallery photo relevant to an amenity, kept
 *  apart so callers can trust the curated category synonyms far more than the
 *  amenity's own (often generic) title words. */
export function amenityKeywordSets(title: string | null | undefined): {
  category: string[];
  title: string[];
} {
  const category = categorize(title);
  const titleWords = (title || '')
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((w) => w.length > 3 && !PHOTO_STOPWORDS.has(w));
  return {
    category: category?.photoKeywords ?? [],
    title: Array.from(new Set(titleWords)),
  };
}
